import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import GroupsIcon from "@mui/icons-material/Groups";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useGetEntityMembersByEntityIdQuery } from "_shared/entity-members/_api/entity-member-api";
import { useGetActiveUserEntityMemberInvitationsQuery } from "legal-entities/invitations/member-invitations/email-based/_api/user-entity-member-invitation-api-service";
import "permissions/__extensions/users/user-extensions";
import useSessionUser from "permissions/session/use-session-user";
import { useState } from "react";
import { Link as RouterLink } from "react-router";

/**
 * Where a leader's "not now" is kept. Per browser, like the dashboard's own arrangement: this is a
 * reminder, not a record, and nothing about it is worth a round trip.
 */
export const buildYourTeamDismissalStorageKey = "attorneyhub.dashboard.build-your-team-dismissed";

export function readBuildYourTeamDismissal(storage: Pick<Storage, "getItem"> | undefined): boolean {
  try {
    return storage?.getItem(buildYourTeamDismissalStorageKey) === "true";
  } catch {
    return false;
  }
}

export function writeBuildYourTeamDismissal(storage: Pick<Storage, "setItem"> | undefined): void {
  try {
    storage?.setItem(buildYourTeamDismissalStorageKey, "true");
  } catch {
    // A reader who cannot save their choice still gets to make it for this visit.
  }
}

/**
 * The dashboard's standing ask for a firm's roster of colleagues.
 *
 * The notification raised when a firm's Attorney Hub proposal is hired is the prompt; this is what
 * makes the prompt survive being ignored. A notification scrolls away and a leader who never opened
 * the bell never saw it at all, while form letter 1 has already told this firm that getting its
 * people onto the platform is the leader's job.
 *
 * It asks two questions rather than looking the prompt up. Whether a notification is still
 * outstanding is not something the browser can ask — the notifications list has no topic-category
 * filter — but whether the roster has been entered is, and that is the thing the banner is actually
 * about. So: somebody who may invite, an organization that is still only them, and nobody invited
 * and waiting. All three stop being true the moment the roster is entered, invitation by invitation
 * or a spreadsheet at a time.
 *
 * The cost of reading it that way is that a sole practitioner who administers their own
 * organization is asked too — which is why "Not now" exists, and why it is remembered.
 */
export default function BuildYourTeamBanner() {
  const user = useSessionUser();
  const [dismissed, setDismissed] = useState(() =>
    readBuildYourTeamDismissal(globalThis.localStorage),
  );

  const mayInvite = user?.canAccessAdministration() === true;
  const legalEntityId = user?.legalEntityId;
  const skip = !mayInvite || !legalEntityId || dismissed;

  // Each read is skipped unless this session may make it. `canAccessAdministration` is true for
  // any one of Member Information, Member Permissions or Member Invitations, and these two
  // endpoints assert the first and the third specifically DASH so a reader who administers only
  // permissions passed the gate above and was answered 403 twice, with a console error each, on
  // a dashboard they are perfectly entitled to. Asking for what each endpoint actually wants
  // keeps the banner silent for them instead.
  const permissions = user?.sessionPermissions;
  const skipMembers = skip || permissions?.includesReadAccessToMemberInformation !== true;
  const skipInvitations = skip || permissions?.includesReadAccessToMemberInvitations !== true;

  const membersQuery = useGetEntityMembersByEntityIdQuery(
    { legalEntityId: legalEntityId?.value ?? "" },
    { skip: skipMembers },
  );
  const invitationsQuery = useGetActiveUserEntityMemberInvitationsQuery(
    // Non-null asserted rather than defaulted: the skip already covers the absent case, and a
    // stand-in id would ask the API about an organization nobody has.
    { entityId: legalEntityId! },
    { skip: skipInvitations },
  );

  if (skipMembers || skipInvitations) return null;
  // Silent while either answer is unknown, and silent if either could not be read. A banner that
  // appeared and then vanished would be read as a mistake, and one shown on a failed read would
  // tell a firm of forty to go and add its people.
  if (membersQuery.data === undefined || invitationsQuery.data === undefined) return null;

  const rosterIsEmpty = membersQuery.data.length <= 1 && invitationsQuery.data.length === 0;
  if (!rosterIsEmpty) return null;

  const putAway = () => {
    setDismissed(true);
    writeBuildYourTeamDismissal(globalThis.localStorage);
  };

  return (
    <Alert
      icon={<GroupsIcon fontSize="inherit" />}
      severity="info"
      action={
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <Button
            component={RouterLink}
            size="small"
            to={`/invite-colleagues/${legalEntityId.value}`}
            variant="contained"
          >
            Add colleagues
          </Button>
          <IconButton aria-label="Not now" onClick={putAway} size="small">
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Stack>
      }
    >
      <AlertTitle>Add your colleagues to Attorney Hub</AlertTitle>
      <Typography variant="body2">
        Everyone at your firm who works on a client&apos;s matters needs their own account and a
        profile listing their credentials. Until they have both, their time cannot be processed for
        billing.
      </Typography>
    </Alert>
  );
}
